/**
 * Marketing and landing content: Download, Home, guided tour copy.
 */
export const staticPages = {
  about: {
    updates: {
      title: 'Check for Updates',
      subtitle: 'Keep Adieuu up to date with the latest features and security patches.',
      webAvailableTitle: 'A new web version is available',
      webAvailableHint: 'Refresh this page to load the latest assets and fixes.',
      webRefreshButton: 'Refresh to update',
    },
    roadmap: {
      title: 'Roadmap',
      subtitle: "What we've built and what we're building, with your input!",
      today: 'Today',
      latestRelease: 'Latest Release',
      communityIdea: 'Community Idea',
      communityIdeaTooltip: 'This feature was proposed and voted on by users like you!',
      upvoteTooltip: 'This feature has received {{count}} community upvotes',
      upvoteTooltip_one: 'This feature has received 1 community upvote',
      upvoteTooltip_other: 'This feature has received {{count}} community upvotes',
      navigateUp: 'Jump to Previous Release',
      navigateDown: 'Jump to Next Release',
      jumpToLatest: 'Jump to Latest Release',
      teamRoadmap: 'Team roadmap',
      suggestedBy: 'Suggested by',
      dateLabel: '{{date}}',
      undatedReleased: 'Released (date unknown)',
      undatedBand: '{{status}} (no target date)',
      commentCount: '{{count}} comments',
      commentCount_one: '1 comment',
      commentCount_other: '{{count}} comments',
      seeMore: 'View Discussion',
      readMore: 'Read more',
      showLess: 'Show less',
      footerTitle: 'And much, much more!',
      footerText:
        "Propose your own feature ideas or vote on others' ideas! We use community upvotes on Features to help determine what to prioritize next. Help shape Adieuu into the platform YOU want!",
      footerCta: 'Create & Vote on Feature Proposals',
      loadError: 'Could not load the roadmap timeline.',
      empty: 'Nothing on the roadmap yet. Check back soon!',
      proposeFeature: 'Propose Feature',
      browseProposals: 'Browse All Proposals',
    },
  },

  download: {
    title: 'Download the desktop app',
    subtitle:
      'Use Adieuu as a native app on Windows, macOS, or Linux. Same privacy and encryption as the browser, with a better fit for daily use.',
    benefitsTitle: 'Why use the desktop app',
    benefitNotifications:
      'Notifications can work even when the app is not in the foreground, so you are less likely to miss messages.',
    benefitSounds:
      'Choose your own notification sound and use custom audio files from your device.',
    benefitNative:
      'Native integration with your operating system: dock or taskbar presence, window management, and a familiar desktop workflow.',
    benefitReliableAudio:
      'Sound playback is less subject to browser autoplay rules, so notifications are more reliable.',
    benefitKeyStorage:
      'Cryptographic keys can be stored in your OS keychain (e.g. Windows Credential Manager, macOS Keychain), not only in browser storage.',
    benefitDedicatedWindow:
      'A dedicated window keeps Adieuu separate from dozens of browser tabs.',
    limitationsTitle: 'Browser limitations',
    limitationTab:
      'In the browser, real-time alerts and sounds generally work only while the tab is open. Closing the tab stops the connection.',
    limitationAutoplay:
      'Browsers may block or delay sounds until you have interacted with the page, which can make notification sounds feel inconsistent.',
    limitationIndexedDb:
      'Browser storage can be cleared when you clear site data or cache, which can affect local keys.',
    limitationNoTray:
      'There is no system tray or dock icon in the browser; the desktop app can sit in the tray for quick access.',
    linksTitle: 'Downloads',
    linksLoading: 'Loading available downloads...',
    linksError: 'Could not load downloads. Please try again later.',
    linksRetry: 'Retry',
    linksNone: 'No downloads are available yet.',
    versionLabel: 'Latest release: v{{version}}',
    releaseDate: 'Released {{date}}',
    recommendedForYou: 'Recommended for your system',
    osWindows: 'Windows',
    osMac: 'macOS',
    osLinux: 'Linux',
    formatNsis: 'Installer (.exe)',
    formatDmg: 'Disk Image (.dmg)',
    formatZip: 'Archive (.zip)',
    formatAppImage: 'AppImage',
    formatDeb: 'Debian (.deb)',
    formatRpm: 'RPM (.rpm)',
    viewSource: 'View source',
    viewSboms: 'Software Bill of Materials (SBOM)',
    viewLinuxSigningKey: 'Linux package signing key (GPG)',
  },

  // Home page
  home: {
    title: 'Welcome to Adieuu',
    subtitle: "Making the internet more human and private, starting with ... messaging!",
    subtitleIdentity: 'Welcome back. Here is an overview of your activity.',
    loading: 'Loading your progress...',
    badgeComingSoon: 'Coming soon',

    public: {
      subtitle: 'Our private Alpha is concluded! Adieuu is now live and in early open beta!',
      betaBadge: 'Early Beta',
      statusTitle: "We're just getting started!",
      statusText:
        "Come check us out! We've spent the last while working on our privacy-focused core and foundations: now we start laying on the features!",
      availableTitle: 'Available Now:',
      availableText:
        'Direct & group (up to 50 people) messages are live. Full end-to-end encryption, lots of customization, and privacy controls everywhere!',
      roadmapCta:
        "See everything we're baking and submit your own feature ideas.",
      roadmapAction: 'Roadmap',
      comingTitle: 'Coming Very Soon:',
      comingText:
        "We're actively building Spaces \u2014 community-driven rooms you can create, discover, and join \u2014 along with a public feed. Once those ship, this page will surface content from the Spaces you care about.",
      ctaTitle: 'Join the beta',
      ctaText:
        'Sign up or log in to get started!',
      ctaAction: 'Log in or Create an Account',
    },

    tabs: {
      welcome: 'Welcome',
      learn: 'Learn',
    },

    // Account-mode action steps
    account: {
      sectionPrimary: 'Get started',
      sectionPrimarySubtitle:
        'Complete these steps to set up your account and create your first Alias.',
      sectionSecondary: 'Recommendations',
      steps: {
        subscribe: {
          title: 'Choose a plan',
          titleFree: "You're on our Free plan",
          titlePaid: "You're subscribed",
          description: 'An active subscription unlocks messaging and social features.',
          descriptionFree:
            'Paid plans unlock exclusive benefits — higher upload limits, extra features, and more. Upgrade anytime, or request community sponsorship if you need help.',
          descriptionPaid:
            'Thanks for supporting Adieuu! Manage your plan or billing details from the subscription page.',
          action: 'View plans',
          upgradeAction: 'View subscription options',
          sponsorshipAction: 'View sponsorship options',
        },
        verifyAge: {
          title: 'Verify your age',
          description: 'Your jurisdiction requires age verification.',
          descriptionFreeCanSkip:
            'Upgrade to a paid plan to skip age verification! Free users require it to reduce botting and compliance liability.',
          descriptionJurisdictionRequired:
            "Your jurisdiction requires age verification. We use the least invasive method possible, and we're the only platform where it can't be attached to your message history or activity.",
          descriptionJurisdiction:
            "Required by your jurisdiction ({{jurisdiction}}). We don't keep any data related to verification, and it's never attached to your Alias (your posts, messages, etc). We're the only platform that fully detaches your account and your alias: we've built our platform specifically so we can handle compliance challenges like this without harming your privacy.",
          disclosureTitle: 'What your jurisdiction requires',
          verificationExplainerTitle: 'How verifications work',
          aliasPrivacy: 'Verifications are attached to your "Account", while your messages, posts, and other activity are attached to your "Alias". No one (not even us) can link an Alias to an Account without knowing your password.',
          aliasPrivacy2: "Plus, we specifically chose our verification partners because they only ever tell us if you complete verification, and if you're over 18 (yes or no): we can't access any of the info you give them to verify. We can't even see your age! All we see is 'Yes, this person is (or isn't) over 18'.",
          aliasPrivacy3: "All of these were intentional choices when we built Adieuu: we're able to preserve your privacy, while other platforms still attach your activity to your email, phone, or ID. There's another benefit here, too - age verification and subscription make it harder (and expensive) for bots and dupe accounts: this means Adieuu has less noise, and more real humans.",
          subscribeFirstTooltip: 'Choose a plan before starting age verification',
          action: 'Start verification',
          resumeAction: 'Resume Verification',
          descriptionJurisdictionNotRequired:
            'Your current jurisdiction does not require age verification. You can skip the verification you started earlier, or finish it if you want to.',
          skipAction: 'Skip verification',
          skipFailed: 'Age verification could not be skipped. Try again.',
        },
        createAlias: {
          title: 'Create your first Alias',
          description: 'Your Alias is how others find you. It is separate from your account login for privacy.',
          action: 'Create Alias',
          subscribeFirstTooltip: 'Choose a plan before creating an Alias',
          verifyAgeFirstTooltip: 'Verify your age before creating an Alias',
        },
        sendFirstMessage: {
          title: "... and that's it!",
          description: "Sign into your Alias, add a friend, and say Adieuu!",
          action: 'Log in to Alias',
          createAliasFirstTooltip: 'Create an Alias before you can start messaging',
        },
      },
      secondary: {
        tour: {
          title: 'Take the tour',
          titleContinue: 'Continue the tour',
          description: 'See where search, your Alias, account settings, and more live in the app.',
          descriptionContinue: "Finish your Account tour: next you'll learn about \"{{nextStep}}\".",
          action: 'Start tour',
          actionContinue: 'Continue tour',
          actionRetake: 'Retake tour',
        },
        mfa: {
          title: 'Add multi-factor authentication (MFA)',
          description: 'Protect your account with an authenticator app or passkey.',
          action: 'Go to security',
        },
        verify: {
          title: 'Verify your email or phone',
          description: 'Confirm your contact method so we can reach you if needed.',
          action: 'Account overview',
        },
        appearance: {
          title: 'Personalise your appearance',
          description: 'Choose a preset theme, customise colours, or explore community themes.',
          action: 'Start tour',
          actionRetake: 'Retake tour',
        },
      },
      allComplete: {
        title: 'You are all set',
        subtitle:
          'You have finished the setup checklist. Log in to your Alias to chat, or jump to account settings below.',
        aliasLogin: 'Log in to your Alias',
        aliasLoginHint: 'Open your Alias profile to switch into Alias mode and start messaging.',
        accountOverview: 'Manage your account',
        security: 'Manage security',
        subscription: 'Manage your subscription',
      },
    },

    // Identity-mode action steps
    identity: {
      sectionStats: 'Your activity',
      sectionPrimary: 'Things to try',
      sectionSecondary: 'Customise',
      stats: {
        conversations: 'Conversations',
        friends: 'Friends',
        messages: 'Messages',
        achievements: 'Achievements',
      },
      steps: {
        addFriend: {
          title: 'Add a friend',
          description: 'Search for people by display name or username and send a friend request.',
          action: 'Find friends',
        },
        startConversation: {
          title: 'Start a conversation',
          description: 'Send an encrypted message to a friend or start a group chat.',
          action: 'New conversation',
        },
        joinSpace: {
          title: 'Join a Space',
          description: 'Spaces are shared communities for groups of people with common interests.',
        },
      },
      secondary: {
        appearance: {
          title: 'Change your appearance',
          description: 'Choose a theme, customise colours, or browse the community marketplace.',
          action: 'Appearance settings',
        },
        editProfile: {
          title: 'Edit your profile',
          description: 'Update your display name, bio, avatar, and other profile details.',
          action: 'Edit profile',
        },
        tour: {
          title: 'Take the tour',
          description: 'A guided walkthrough of messaging, friends, settings, and more.',
          action: 'Start tour',
          actionRetake: 'Retake tour',
        },
      },
    },

    // Learn tab (shared across modes)
    learn: {
      title: 'Learn about Adieuu',
      goBack: 'Go Back',
      navLabel: 'Learn',
      copyLink: 'Copy link to section',
      linkCopied: 'Link copied to clipboard',
      permalinkLabel: 'Permalink to this section',
      search: {
        placeholder: 'Search all topics…',
        label: 'Search Learn topics',
        noResults: 'No matching topics found.',
        resultsLabel: 'Search results',
        resultMeta: '{{tab}} · {{category}}',
      },
      tabs: {
        about: {
          label: 'About Adieuu',
          categories: {
            overview: {
              label: 'Overview',
              sections: {
                whatIsAdieuu: {
                  title: 'What is Adieuu?',
                  content: [
                    'Adieuu is a messaging and social platform built around privacy and accessibility, specifically with humans in mind. We believe the internet was generally better before it was taken over by big data conglomerates, bots and AI: it was more human, more personal, and more altruistic. Bots, AI, etc have their place - but as they increasingly dominate internet traffic, we believe there needs to be a space online that remains distinctly human.',
                    "To that end, we're modernizing our favorte things from the Internet, both early and recent, and adapting them for the privacy needs of the online world today. You'll find many features you may already be familiar with from other apps, alongside some functionality that disappeared from many platforms when the internet became profit-focused: all further refined with 'privacy first' in mind."
                  ],
                },
                mission: {
                  title: 'Adieuu\'s Mission',
                  content: [
                    'We believe private communication is a fundamental right. Adieuu exists to prove that a social platform can be secure, transparent, community-driven, and sustainable - all without relying on advertising or data collection. Further, we want online spaces to feel more human. As internet traffic is increasingly overtaken by bots and AI (over half of all web-traffic is now by bots!) and other apps cater to the AI-first user, our approach is the opposite: we focus on the building a space online the remains distinctly human.',
                    'Further, we want online spaces to feel more human. As internet traffic is increasingly overtaken by bots and AI (over half of all web-traffic is now by bots!) and other apps cater to the AI-first user, our approach is the opposite: we focus on the building a space online the remains distinctly human.',
                  ],
                },
                privacyStance: {
                  title: 'Pay with a subscription, not surveillance!',
                  content: [
                    'Adieuu is funded entirely by the people who use it. There are no ads, no data sales, no tracking pixels, and no incentive for us to keep you in the app longer. We do have to make some compromises on our free plans; in fact, today you might see some features missing from our free plan that other services\' free plans include (like video streaming). The reason for that is simply that some features, especially high-bandwidth ones like large files, videos and livestreaming, cost a lot to offer. If we were to offer them for free, we would have some of the same problemsseek excessive outside funding or compromise on our commitments to your privacy.',

                    'If we were to offer many of these high-cost features for free, we wouldn\'t be able to sustain it without having to make compromises elsewhere - such as seeking excessive outside funding or otherwise compromising on your privacy like many other companies have done. Alternatively, we could charge higher subscription costs instead, but that means paid users are subsidizing the cost of free user\'s features. That didn\'t seem particularly fair since that removes paid users\' liberty - they should be able to choose whether or not they are paying for someone else\'s account (and, by the way, we offer the ability to Sponsor other users\'s subscriptions for users who do choose to help others!).',
                    
                    'Instead, we felt the most organic path forward was to offer as much for free as we felt we could reasonably sustain for all users, and then place the highest-cost, nice-to-have features behind payment. To that end, we generally tend to only paywall features that don\'t affect security/privacy and are high-cost for us to operate (or are purely cosmetic). This helps us ensure private communications stay accessible for everyone, in a much more sustainable and organic way.',

                    'So why subscriptions?',
                    'We felt subscriptions had the least conflicts of interest between you and us: servers cost money as long as they are online, and you only pay us when our service is good enough to justify it. That keeps pressure on us to continue innovating and delivering in order to earn your renewal, and it makes the math is easy to calculate for both you and us. Single payments don\'t scale, since servers keep running indefinitely (which is why we won\'t offer lifetime plans forever), and with MTX companies tend to become incentivized to nickel-and-diming you. We felt subscriptions was the most reasonable balance between your interests and ours.'
                  ],
                },
                openSource: {
                  title: 'Radical Transparency & Open Source',
                  content: [
                    'We are determined to operate differently than most companies. Our source code is publicly available for audit, review, and self-hosting. We publish a software bill of materials (SBOM) with every release and maintain a public roadmap directly shaped by your votes and input. We further plan to regularly publish our financials, too - you will be able to see what we make, who/what it is spent on, and how we grow Adieuu.',
                    'Beyond this, we do our best to be available to everyone for questions, feedback, and ideas. We will always answer any questions we can (as long as they do not affect another person\'s privacy).'
                  ],
                },
              },
            },
            platform: {
              label: 'Platform',
              sections: {
                securityByDesign: {
                  title: 'Foundational Emphasis on Security & Privacy',
                  content: [
                    'Security begets privacy, begets true freedom of speech. While nothing is impenetrable, we do our best to ensure we exceed best practices and standards across the board. Our founders have decades of experience in infosec & cybersecurity (with an emphasis on consumer privacy and security), and our team works hard to maintain a proactive and forward-thinking strategy when it comes to protecting the interests of our users.',
                    'Further, we try to encourage better security practices by our users by offering discounts and other incentives when they take steps to protect themselves. For example, we offer discounts on subscriptions to users that add multi-factor auth (MFA) to their accounts (and a larger discount when that is combined with passkeys or hardware-based keys like Yubikeys).'
                  ],
                },
                encryptionAndAuth: {
                  title: 'Authentication',
                  content: [
                    'Logins are intentionally passwordless, removing the potential for password stuffing or related attacks. Multi-factor authentication uses WebAuthn passkeys or one-time codes: when paired, this effectively requires multiple mediums you control to be compromised before your Adieuu account can be targeted.',
                  ],
                },
                uniqueArchitectureAccountsAliases: {
                  title: 'Unique Architecture: Accounts vs Aliases',
                  content: [
                    'Even if someone is able to authenticate as you, they still have no way of directly accessing your messages or activity. This is because Adieuu intentionally separates your real information (your "Account") from your messages and other activity (your "Alias") with cryptography. This works both ways: even if our database is exposed, your Alias cannot be linked back to your Account, and your Account cannot be linked to your Alias without knowing a unique passphrase you set during creation. This gives you multiple layers of protection against the accidental or malicious exposure of your data.',
                  ],
                },
                accountsAliasesAgeVerification: {
                  title: 'Accounts vs Aliases: Age Verification',
                  content: [
                    'As noted above, our unique, cryptographic separation of your Account (real info) and Alias (messages, profile, activity) ensures the two can\'t be linked (even if our database is breached). It also ensures we are able to comply with legislation in your jurisdiction that might require age assurance or verification without compromising your privacy. For starters, we always use the least invasive methods legally available, and where necessary use a third-party provider that never gives us any of your information (not even your age!) and discards all data immediately. All we see and store is a pass/fail result (and basic metadata like timestamps), and since that is attached only to your Account, it means it cannot be linked to your Alias, messages, or activity. We\'re able to comply with the law, and you\'re able to stay private.',
                  ],
                },
                endToEndEncryption: {
                  title: 'End to End Encryption (E2EE)',
                  content: [
                    'In DMs and group conversations (up to 50 members), all content is always E2E encrypted: this means it\'s encrypted before it leaves your device, and only readable to the people you send it to (who decrypt it on their own devices). Adieuu staff cannot read your messages, nor could any third-party (including in case of database breach).',
                    'Spaces (larger communities with no user limit) have optional E2EE: public Spaces can have E2EE enabled for categories or channels, but their Home & core content is generally not E2EE. Unlisted and Hidden Spaces can fully E2EE their Space (including name, metadata, Home, and all content) via Community Ciphers (shared keys known only to members of the Space).',
                  ],
                },
                spacesAndCommunities: {
                  title: 'Spaces and communities',
                  content: [
                    'Spaces are shared communities for groups of people with common interests. This might be a larger project or topic ("bicycles" or "prepping"), a focused group (like a gaming clan or coffee club), or any other mid-large group of people with shared interests. Content within a Space is encrypted, and you interact through your Alias, keeping your real identity private. Think of them as group hubs where you can have ongoing conversations with a broader community.',
                  ],
                },
                voiceAndVideo: {
                  title: 'Voice and video calls',
                  content: [
                    'Adieuu supports fully end-to-end encrypted voice and video calls, including screen sharing! In our tests thus far, we have been able to sustain well over 60FPS calls with up to 320kbps audio quality (for studio and other high-quality mics that can push that!) with very low latency..',
                  ],
                },
                desktopApp: {
                  title: 'Why use the desktop app?',
                  content: [
                    'The Adieuu desktop app runs on Windows, macOS, and Linux. While it shares largely the same codebase as the web app (browser), there are specific things we\'re able to support in the desktop app that a browser just doesn\'t give us access to. For example, in desktop today we offer system-level notifications (and a lot of sound customizations!), minimize to tray, popout windows/threads, and more direct OS keychain storage for your cryptographic keys (a big win for security). Further, many people have a variety of addons or extensions in their browsers - by using the desktop app, your Adieuu sessions are entirely isolated from your primary browser, providing you an additional level of protection and privacy.',
                  ],
                },
              },
            },
            gettingStarted: {
              label: 'Getting started',
              sections: {
                accountAndAlias: {
                  title: 'Account vs Alias',
                  content: [
                    'Adieuu has two layers: Account and Alias. Your Account holds your email or phone, billing, and security settings. Your Alias is your social presence: it has its own username, profile, and conversations. The two are cryptographically separated, so sharing your Alias never reveals the personal details behind your Account.',
                  ],
                },
                createAlias: {
                  title: 'Create your first Alias',
                  content: [
                    'Once you have an active subscription, you can create an Alias. Pick a username and display name, set up a profile, and you are ready to message, add friends, and join Spaces. If your subscription tier allows it, you can create multiple Aliases for different contexts.',
                  ],
                },
                firstSteps: {
                  title: 'Your first steps',
                  content: [
                    'After creating your Alias, try adding a friend by searching for their username, joining a Space that interests you, or starting a direct conversation. Everything you send is encrypted before it leaves your device.',
                  ],
                },
                readyToStart: {
                  title: 'Ready to get started?',
                  content: [
                    'Creating an account takes seconds. All you need is an email or phone number. From there, choose a plan, create your Alias, and start talking.',
                  ],
                },
              },
            },
          },
        },
        privacy: {
          label: 'Privacy & Security',
          categories: {
        privacyBasics: {
          label: 'Privacy (Basics)',
          sections: {
            aliasPrivacy: {
              title: 'How Aliases protect your privacy',
              content: [
                'Adieuu separates your Account (email or phone, billing) from your Alias (your public profile). The two are cryptographically unlinkable, meaning no one, not even Adieuu, can connect them. Your real-world identity stays private while you communicate freely under a name you choose.',
              ],
            },
            ageVerification: {
              title: 'Age verification without compromising identity',
              content: [
                'Where required by law, Adieuu verifies your age through a third-party provider. The verification result is stored on your Account, never on your Alias, and is never shared with the people you talk to. Your ID documents are processed by the verification provider and are not stored by Adieuu. We only ever receive a pass or fail result.',
              ],
            },
            dataCollection: {
              title: 'What data Adieuu collects (and what it does not)',
              content: [
                'Adieuu collects only what is needed to operate the service: an email or phone number for authentication, and the encrypted messages you send (which we cannot read). We do not build advertising profiles, track your conversations, or sell any of your data. Usage analytics, when collected, are aggregated and anonymous.',
              ],
            },
            noTracking: {
              title: 'No tracking, no profiling, no algorithmic feeds',
              content: [
                'There are no tracking pixels, no behavioural analytics, and no feed algorithm deciding what you see. Adieuu does not monitor what you read, how long you stay, or who you talk to. Your attention is yours.',
              ],
            },
          },
        },
        privacyAdvanced: {
          label: 'Privacy (Advanced)',
          sections: {
            metadataProtection: {
              title: 'Metadata protection and traffic analysis resistance',
              content: [
                'Message content is only part of the picture. Who talks to whom and when can be just as revealing. Adieuu minimises metadata exposure by keeping contact graphs encrypted and avoiding long-lived identifiers in network traffic. We are actively researching additional techniques such as sealed sender and padding to further resist traffic analysis.',
              ],
            },
            unlinkability: {
              title: 'Cryptographic unlinkability between Accounts and Aliases',
              content: [
                'The separation between your Account and your Alias is enforced cryptographically, not just by policy. Alias credentials are derived through a one-way process that prevents correlation, even by Adieuu infrastructure. A database breach cannot link your Alias activity back to your account email or phone number.',
              ],
            },
            forwardSecrecy: {
              title: 'Forward secrecy',
              content: [
                'Adieuu supports forward secrecy, which means that encryption keys are rotated regularly during a conversation. If a key were ever compromised, only a small window of messages would be affected. Past and future messages remain protected.',
              ],
            },
          },
        },
        securityBasics: {
          label: 'Security (Basics)',
          sections: {
            encryption: {
              title: 'End-to-end encryption',
              content: [
                'Every message is encrypted on your device before it leaves. Only you and your intended recipients hold the keys to decrypt it. Adieuu uses proven, well-audited cryptographic algorithms so that no intermediary, including Adieuu servers, can read your conversations.',
              ],
            },
            securityModel: {
              title: 'Platform security model',
              content: [
                'Adieuu is built on a zero-trust architecture. Session tokens are short-lived and scoped. Passwordless authentication eliminates credential-stuffing attacks. Multi-factor authentication adds a second layer of protection. All data at rest is encrypted, and all traffic uses TLS.',
              ],
            },
            passwordless: {
              title: 'Passwordless authentication',
              content: [
                'You never create a password for Adieuu. Instead, you sign in with a passkey (WebAuthn) or a one-time code sent to your email or phone. This eliminates entire classes of attack: there is no password to phish, reuse, or stuff into a breach database.',
              ],
            },
          },
        },
        securityAdvanced: {
          label: 'Security (Advanced)',
          sections: {
            postQuantum: {
              title: 'Post-quantum cryptography',
              content: [
                'Quantum computers may eventually be able to break today\'s public-key cryptography. Adieuu already uses a hybrid approach that combines classical algorithms with post-quantum key encapsulation (ML-KEM). Your messages are protected against both current attacks and future quantum threats, without sacrificing compatibility or performance.',
              ],
            },
            keyManagement: {
              title: 'Key management and device trust',
              content: [
                'Your encryption keys are generated and stored on your own devices, never uploaded in plaintext. When you add a new device, a secure key-transfer protocol ensures that your existing conversations remain accessible without exposing key material to the server. Revoking a device immediately removes its access to future messages.',
              ],
            },
            openSourceSecurity: {
              title: 'Security through transparency',
              content: [
                'Our cryptographic implementation is open source. Security researchers can audit the code, review our choices, and report issues through our responsible disclosure process. We publish software bills of materials (SBOMs) with every release so you can verify exactly what is in each build.',
              ],
            },
          },
        },
        accountsAndAliases: {
          label: 'Accounts and Aliases',
          sections: {
            whySeparation: {
              title: 'Why Adieuu separates Accounts from Aliases',
              content: [
                'Your Account is the administrative layer. It holds your email or phone, billing, and security settings. Your Alias is your social presence: it is what other people see. By keeping these separate at a cryptographic level, Adieuu ensures that sharing your public profile never reveals the personal details behind your Account.',
              ],
            },
            switchingAliases: {
              title: 'Switching between Aliases',
              content: [
                'You can create multiple Aliases under one Account, each with its own display name, avatar, and theme. Switching between them is instant and does not require signing out. Other users cannot tell that two Aliases belong to the same Account, because even Adieuu cannot make that connection.',
              ],
            },
            aliasPassword: {
              title: 'Your Alias password',
              content: [
                'When you create an Alias, you set a password that only you know. This password is what makes unlinkability possible. It is never sent to the server; it is used locally to derive the cryptographic keys that connect your Account to your Alias. Without it, there is no link.',
              ],
            },
            lostAccess: {
              title: 'What happens if you lose access',
              content: [
                'If you lose access to your account email or phone, Adieuu provides a recovery flow that verifies your identity through a secondary method or recovery codes. Because encryption keys live on your devices, maintaining at least one trusted device is the most reliable way to preserve access to your message history.',
              ],
            },
          },
        },
        subscriptions: {
          label: 'Subscriptions',
          sections: {
            whyPaid: {
              title: 'Why Adieuu requires a subscription',
              content: [
                'Subscriptions are how Adieuu stays independent. By funding the platform directly, users remove the need for advertising, data monetisation, or venture capital that might compromise privacy down the line. A subscription also raises the cost of creating bot or spam accounts, which keeps the platform healthier for everyone.',
              ],
            },
            plans: {
              title: 'Available plans',
              content: [
                'Adieuu offers tiered plans (Access and Insider) that unlock different levels of features, such as the number of Aliases you can create, upload limits, and stream quality. All plans include the same strong encryption and privacy guarantees. Unauthenticated visitors can browse public Spaces but cannot post or message without a subscription.',
              ],
            },
            whatUnlocked: {
              title: 'What a subscription unlocks',
              content: [
                'A subscription lets you create Aliases, send messages, participate in Spaces, add friends, and access voice and video calls. It also supports the ongoing development of Adieuu so we never need to rely on advertising or data sales.',
              ],
            },
            billing: {
              title: 'Billing and cancellation',
              content: [
                'Subscriptions are billed monthly or annually through your chosen payment method. You can cancel at any time from your Account settings, and your paid features remain active until the end of the current billing period. Adieuu does not charge cancellation fees.',
              ],
            },
          },
        },
          },
        },
        idVerification: {
          label: 'ID Verification',
          categories: {
            whyRequired: {
              label: 'Why verification is required',
              sections: {
                jurisdiction: {
                  title: 'When your jurisdiction requires verification',
                  content: [
                    'Some jurisdictions have laws requiring age verification before you can access social or messaging platforms. Adieuu enforces these requirements based on your location and applicable legislation. If verification is required, you will be prompted to complete it before creating or accessing an Alias.',
                  ],
                },
                subscriptionGate: {
                  title: 'Verification and subscriptions',
                  content: [
                    'An active subscription (Access or Insider) is required to create or log into an Alias. Age verification is a separate compliance step that may be required in addition to an active plan. You will need to subscribe first, then verify if your jurisdiction requires it.',
                  ],
                },
                geofencing: {
                  title: 'Regional availability',
                  content: [
                    'Due to local legislation, Adieuu may not be available in some jurisdictions. When a region is blocked, the service cannot be accessed regardless of verification status. We publish our jurisdiction list transparently so you know what applies to you.',
                  ],
                },
                whyNotJustBlock: {
                  title: 'Why not just block under-18s without verification?',
                  content: [
                    'Age-gating by self-declaration (clicking "I am over 18") does not satisfy the legal requirements in many jurisdictions. Where the law mandates verification, Adieuu complies, but we do it in a way that preserves your privacy: verification lives on your Account, not your Alias, and we never see your documents or exact age.',
                  ],
                },
              },
            },
            jurisdictions: {
              label: 'Jurisdictions requiring verification',
              sections: {
                catalog: {
                  title: 'Where verification is required',
                  content:
                    'The jurisdictions below require some form of age or identity verification under applicable legislation. This reference is for transparency and is not legal advice. Requirements and compatible methods reflect what Adieuu tracks for enforcement.',
                  variant: 'jurisdictionCatalog',
                },
              },
            },
            howItWorks: {
              label: 'How verification works',
              sections: {
                providerFlow: {
                  title: 'Third-party verification provider',
                  content: [
                    'Adieuu verifies your age through a third-party provider (currently VerifyMy). When you start verification, a secure session opens where you complete the provider\'s checks. Adieuu receives only the verification status needed to enforce access. We cannot see your ID documents, your exact age, or any other details you provide to the verification provider.',
                  ],
                },
                sessionStates: {
                  title: 'During verification',
                  content: [
                    'After starting, complete the verification in the opened window. The session remains active while you complete the provider\'s checks. If your session expires before completion, you can retry after a short cooldown period.',
                  ],
                },
                outcomes: {
                  title: 'Outcomes and retries',
                  content: [
                    'If your verification status is a pass, you can proceed to create or log into an Alias immediately. If it fails or expires, you can retry once the cooldown period ends. The cooldown exists to prevent abuse of the verification system.',
                  ],
                },
              },
            },
            privacyAndChoice: {
              label: 'Privacy and choice',
              sections: {
                dataHandling: {
                  title: 'How your verification data is handled',
                  content: [
                    'The verification status (pass or fail, nothing more) is stored on your Account. It is never attached to your Alias and is never shared with other users. Your ID documents are processed entirely by the verification provider and are not stored or accessed by Adieuu. This is a deliberate architectural choice: verification satisfies legal requirements while the Alias system preserves your privacy.',
                  ],
                },
                voluntaryOptIn: {
                  title: 'Voluntary verification',
                  content: [
                    'If we cannot determine your jurisdiction automatically, you are responsible for adhering to local age verification laws. You can opt in to verification voluntarily by choosing the jurisdiction whose rules should apply to you.',
                  ],
                },
                unresolvedJurisdiction: {
                  title: 'When jurisdiction is unknown',
                  content: [
                    'When your jurisdiction cannot be determined automatically, Adieuu shows an advisory rather than blocking access outright. You can choose to verify voluntarily, or proceed while ensuring you comply with local laws yourself.',
                  ],
                },
              },
            },
          },
        },
      },
    },

    // Legacy keys kept for backward compat during migration
    features: {
      encryption: {
        title: 'End-to-End Encryption',
        description: 'All messages are encrypted with strong cryptography. Only you and your recipients can read them.',
      },
      crossPlatform: {
        title: 'Cross-Platform',
        description: 'Access your messages from web, desktop, or mobile. Stay connected wherever you are.',
      },
      nativeDesktop: {
        title: 'Native Desktop App',
        description: 'Experience Adieuu as a native desktop application with system notifications and offline support.',
      },
      passwordless: {
        title: 'No Password Required',
        description: 'Passwordless authentication keeps your account secure without the hassle of remembering passwords.',
      },
      privacy: {
        title: 'Privacy First',
        description: "We collect only what's necessary. Your data is yours.",
      },
    },
    onboarding: {
      title: 'Get started',
      subtitle:
        'Adieuu has two sign-ins: your Account (where your personal info lives, like email or phone) and your Alias (your public profile you use for chatting and other activities). Complete the steps below to secure your Account and start messaging.',
      loading: 'Loading your progress...',
      badgeComingSoon: 'Coming soon',
      items: {
        tour: {
          title: 'Take the tour',
          description: 'See where search, your Alias, account settings, and more live in the app.',
          action: 'Start tour',
          actionRetake: 'Retake tour',
        },
        mfa: {
          title: 'Add multi-factor authentication (MFA)',
          description: 'Protect your account with an authenticator app or passkey.',
          action: 'Go to security',
        },
        verify: {
          title: 'Verify your email or phone',
          description: 'Confirm your contact method so we can reach you if needed.',
          action: 'Account overview',
        },
        alias: {
          title: 'Create your first Alias',
          description: 'Your Alias is how others find you. It is separate from your account login for privacy.',
          action: 'Alias settings',
        },
        appearance: {
          title: 'Personalise your appearance',
          description: 'Choose a preset theme, customise colours, or explore community themes.',
          action: 'Start tour',
          actionRetake: 'Retake tour',
        },
        age: {
          title: 'Verify your age',
          description: 'Age verification will be available here soon.',
        },
      },
    },
  },

  // Guided tour (product walkthrough)
  tour: {
    steps: {
      welcome: {
        title: 'Welcome to Adieuu!',
        descriptionWeb:
          "You are currently signed into your Account. From here you can handle billing, security, and settings. You'll notice you're unable to add friends, send messages, etc: those are done with your Alias, which is completely separate from your Account for your privacy. This is one of the special things about Adieuu! We'll talk more about Aliases later on: if you already have an Alias, you can sign into it and start the Alias Tour to learn more about those features.",
        descriptionDesktop:
        "You are currently signed into your Account. From here you can handle billing, security, and settings. You'll notice you're unable to add friends, send messages, etc: those are done with your Alias, which is completely separate from your Account for your privacy. This is one of the special things about Adieuu! We'll talk more about Aliases later on: if you already have an Alias, you can sign into it and start the Alias Tour to learn more about those features!",
        descriptionMobile:
        "You are currently signed into your Account. From here you can handle billing, security, and settings. You'll notice you're unable to add friends, send messages, etc: those are done with your Alias, which is completely separate from your Account for your privacy. This is one of the special things about Adieuu! We'll talk more about Aliases later on: if you already have an Alias, you can sign into it and start the Alias Tour to learn more about those features!",
      },
      getStarted: {
        title: 'Get started',
        description:
          'This is your home base. Follow these steps to set up your account and start messaging.',
      },
      search: {
        title: 'Search',
        description:
          'Search for people by display name or username. Only public content and profiles are visible until you create an Alias. You will need to sign in to an Alias to add friends, message, and more.',
      },
      sidebarTabs: {
        title: 'Friends and conversations',
        description:
          'Your sidebar will populate once you sign into your Alias. Once it does, you will be able to easily swap between DMs, Group Conversations, and Spaces.',
      },
      identity: {
        title: 'Your Alias',
        description:
          "This is where you'll log in to (or create) an anonymous Alias to start messaging, posting, etc. Your activity can't be tracked back to your Account and real info! You could do this now, but we have a few more things worth mentioning on this tour - up to you!",
      },
      account: {
        title: 'Account menu',
        description:
          'Access your account settings, subscription, referrals, and sign out from here.',
      },
      accountLink: {
        title: 'Account page',
        description:
          "Click here to open your Account page, where you'll manage your profile, security, and sessions. Let's take a look (click Next to advance the tour).",
      },
      accountOverview: {
        title: 'Overview',
        description:
          'Your overview shows your email, phone, and account standing. We use your email (or phone, whatever you add) to reach out to you for updates that affect your account, authentication, etc. We never share you info. See our Privacy Policy for details.',
      },
      accountAuthentication: {
        title: 'Authentication',
        description:
          'Set up multi-factor authentication here using an authenticator app or a passkey (hardware keys are supported!). MFA is the single best thing you can do to protect your account. We believe so strongly in this, we offer billing discounts for users that add MFA (even more for hardware keys like Yubikeys!)',
      },
      accountSessions: {
        title: 'Sessions',
        description:
          "View all the places your account is signed in at ('sessions'), and revoke any you don't recognize. If something looks off, you can sign out all other sessions at once.",
      },
      accountDataExport: {
        title: 'Data export',
        description:
          "Want to know what data we have about you? Browse or download everything! You may notice there's no activity, messages, friends, etc: we have no idea about any of that, by design. If we receive a subpoena for your account, everything you see here is all that we could provide.",
      },
      subscription: {
        title: 'Subscription',
        description:
          "Let's check out your subscription. Click Subscription in the menu (hit Next to continue the tour).",
      },
      subscriptionOverview: {
        title: 'Your plan',
        description:
          "This is where you manage your plan. You can see your current tier, upgrade, or check your billing status. If you select a paid plan, you'll see a link to your Stripe billing portal where you can manage billing details (we do not store any card or other billing info in our servers).",
      },
      subscriptionPromo: {
        title: 'Promo codes',
        description:
          'Have a promo code? Redeem it here for discounts and more!',
      },
      subscriptionSponsorships: {
        title: 'Sponsorships',
        description:
          "One of our goals is to make privacy as accessible as possible. Not everyone can afford a paid subscription. If that's you, you can use this tab to ask for someone else to gift you a paid subscription. Alternatively, if you've got some cash to spare and grok our mission, maybe consider sponsoring someone that needs it? Adieuu staff gift a few sponsorship requests each month - so if you need it, ask, and maybe you'll get some help!",
      },
      roadmap: {
        title: 'Roadmap',
        description:
          "Let's visit the Roadmap! Click Roadmap in the sidebar, or hit Next.",
      },
      roadmapOverview: {
        title: 'What we are building',
        description:
          "The Roadmap shows what we've released recently and what we're actively working on. For example, here's one of the things in our most recent release. You can scroll up to look back in our history, or down to see what's coming next! Our Roadmap is heavily influenced by community input. In fact .... (click Next)",
      },
      roadmapProposals: {
        title: 'Community proposals',
        description:
          'We accept bug reports and feature proposals from the community, and use upvotes to help prioritise what we build next. Your voice matters! Tell us how we can make Adieuu better for YOU!',
      },
      logout: {
        title: 'Sign out',
        description:
          "Whenver you would like, you can sign out via this menu option. When logged into your Alias, you'll see a similar option.",
      },
      complete: {
        title: 'You are all set!',
        description:
          'Head back to the Get Started checklist to continue setting up your account.',
      },
    },
    appearance: {
      welcome: {
        title: 'Personalise Adieuu',
        description: 'Let us show you how to customise the look and feel of Adieuu to match your style.',
      },
      nav: {
        title: 'Finding Appearance',
        description: 'Open your account menu and select Appearance to access theme settings.',
      },
      presets: {
        title: 'Preset themes',
        description: 'Choose from several built-in themes. Click one to apply it instantly.',
      },
      editor: {
        title: 'Customise colours',
        description:
          "Select a theme above, then open Customise to tweak individual colours. When you're done, don't forget to save your changes!",
      },
      communityLink: {
        title: 'Community marketplace',
        description: 'Head to the Community Themes page to browse themes shared by other users.',
      },
      communitySearch: {
        title: 'Search themes',
        description: 'Use the search bar to find community themes by name, description, or tag. Results update as you type.',
      },
      btnPreview: {
        title: 'Detailed Color List',
        description: 'Click the eye icon to open a full list of every colour in this theme before committing to it.',
      },
      btnIdentity: {
        title: 'Set as Alias theme',
        description: 'This icon applies the theme only to your current Alias session, giving each Alias its own look. You can further customise per-Alias themes from the Appearance page under your Alias settings.',
      },
      btnIdentityHint: {
        title: 'Per-Alias themes',
        description: 'If you are signed in to one of your Aliases, you will also see an icon here to apply a theme only to that Alias. This is great for creating visual separation when you own multiple Aliases. You can also manage per-Alias themes from the Appearance page under your Alias settings.',
      },
      btnUpvote: {
        title: 'Upvote',
        description: 'Like a theme? Give it an upvote so others can find it more easily. You cannot upvote your own themes.',
      },
    },
    resumeToast: {
      title: 'Tour paused',
      description: 'You closed the tour early. Pick up where you left off whenever you are ready.',
      action: 'Resume tour',
    },
  },
} as const;
